import { motion, AnimatePresence } from 'framer-motion';
import type { Toast as ToastType } from '../hooks/useToast';

interface ToastStackProps {
  toasts: ToastType[];
  onDismiss: (id: string) => void;
}

const typeStyles: Record<ToastType['type'], { bg: string; border: string; icon: string }> = {
  info: {
    bg: 'bg-blue-50/95',
    border: 'border-blue-200',
    icon: 'text-blue-500',
  },
  warning: {
    bg: 'bg-amber-50/95',
    border: 'border-amber-200',
    icon: 'text-amber-500',
  },
  error: {
    bg: 'bg-red-50/95',
    border: 'border-red-200',
    icon: 'text-red-500',
  },
  success: {
    bg: 'bg-green-50/95',
    border: 'border-green-200',
    icon: 'text-green-500',
  },
};

function ToastIcon({ type }: { type: ToastType['type'] }) {
  switch (type) {
    case 'info':
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
    case 'warning':
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="12" y1="9" x2="12" y2="13" strokeLinecap="round" />
          <line x1="12" y1="17" x2="12.01" y2="17" strokeLinecap="round" />
        </svg>
      );
    case 'error':
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    case 'success':
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
  }
}

function ToastItem({ toast, onDismiss }: { toast: ToastType; onDismiss: (id: string) => void }) {
  const styles = typeStyles[toast.type];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 100, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={`${styles.bg} ${styles.border} border rounded-xl backdrop-blur-md shadow-lg p-4 min-w-[320px] max-w-[400px]`}
      data-testid={`toast-${toast.type}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 mt-0.5 ${styles.icon}`}>
          <ToastIcon type={toast.type} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-brand-navy">{toast.title}</p>
          {toast.message && (
            <p className="text-xs text-gray-600 mt-1">{toast.message}</p>
          )}
          {toast.action && (
            <button
              onClick={() => {
                toast.action!.onClick();
                onDismiss(toast.id);
              }}
              className="text-xs font-medium text-brand-gold hover:text-brand-gold-dark mt-2 transition-colors"
            >
              {toast.action.label}
            </button>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={() => onDismiss(toast.id)}
          className="flex-shrink-0 p-1 hover:bg-black/5 rounded-full transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </motion.div>
  );
}

/**
 * Toast notification stack renderer.
 *
 * Renders toasts in the top-right corner, positioned below ConnectionState.
 * Uses framer-motion for enter/exit animations (slide-in from right, fade-out).
 * Z-index z-60 to be above chat overlay (z-50).
 */
export function ToastStack({ toasts, onDismiss }: ToastStackProps) {
  return (
    <div
      className="fixed top-16 right-4 z-[60] flex flex-col gap-2"
      data-testid="toast-stack"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
}
